import type { CardPriority, ItemStatus, ContextOwner, HeadingScope, LinkSync, MoscowLevel } from '@blueprint/domain'
import { create } from 'zustand'

export interface Filters {
  search: string
  status: ItemStatus | ''
  priority: CardPriority | ''
  moscow: MoscowLevel | ''
  owner: string
  tag: string
  release: string
}

export const EMPTY_FILTERS: Filters = {
  search: '',
  status: '',
  priority: '',
  moscow: '',
  owner: '',
  tag: '',
  release: '',
}

export type EditorTarget =
  | { mode: 'edit'; cardId: string }
  | { mode: 'create'; rowId: string; columnId: string }

export type PromptScope = { kind: 'row' | 'column'; id: string }

/** What the name dialog is naming: a new item of some kind, a new project, or a rename. */
export type BoardNameDialogKind = 'create' | 'create-mindmap' | 'create-workflow' | 'create-project' | 'rename' | 'rename-project'

export type ProjectDialog = { kind: 'members' | 'delete'; projectId: string } | { kind: 'move'; boardId: string }

export interface ConfirmRequest {
  title: string
  message: string
  /** Shown in bold beneath the message, e.g. the number of cards that will be deleted. */
  consequence?: string
  confirmLabel: string
  undoable: boolean
  onConfirm: () => void
}

interface UiStore {
  /** The landing page is the board list; opening a board switches to 'board'. */
  view: 'boards' | 'board' | 'mindmap' | 'workflow'
  switcherOpen: boolean
  /** While true the board mounts every card and stops clipping, so an export is complete. */
  exporting: boolean
  filters: Filters
  editor: EditorTarget | null
  moveCardId: string | null
  /** Card whose committed values are shown in the standalone prompt preview. */
  promptCardId: string | null
  promptScope: PromptScope | null
  setPromptScope: (scope: PromptScope | null) => void
  /** The board, swimlane, row or column whose architecture context is being edited. */
  contextOwner: ContextOwner | null
  /** The heading whose notes dialog is open. */
  headingNotes: { scope: HeadingScope; id: string } | null
  setHeadingNotes: (target: { scope: HeadingScope; id: string } | null) => void
  setContextOwner: (owner: ContextOwner | null) => void
  /** The board Reconciliation panel, optionally narrowed to one element's findings. */
  reconciliation: { targetId: string | null } | null
  setReconciliation: (reconciliation: { targetId: string | null } | null) => void
  importOpen: boolean
  boardNameDialog: BoardNameDialogKind | null
  confirm: ConfirmRequest | null
  appearanceOpen: boolean
  /** When on, "+ Add card" inserts straight onto the board instead of opening the editor. */
  quickInsert: boolean
  /** Whether label and colour edits are carried between a mindmap and the story maps made from it. */
  linkSync: LinkSync
  /** A just-inserted card whose title should open for editing, cleared once it has. */
  focusCardId: string | null
  minimapOpen: boolean
  /** Board scale. Drives the metric tokens and the JS card-slot height together. */
  zoom: number

  setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void
  clearFilters: () => void
  openEditor: (target: EditorTarget) => void
  closeEditor: () => void
  openMove: (cardId: string) => void
  closeMove: () => void
  openPrompt: (cardId: string) => void
  closePrompt: () => void
  setImportOpen: (open: boolean) => void
  setBoardNameDialog: (kind: BoardNameDialogKind | null) => void
  requestConfirm: (request: ConfirmRequest) => void
  closeConfirm: () => void
  setAppearanceOpen: (open: boolean) => void
  setQuickInsert: (on: boolean) => void
  setLinkSync: (change: Partial<LinkSync>) => void
  setFocusCardId: (cardId: string | null) => void
  setMinimapOpen: (open: boolean) => void
  setZoom: (zoom: number) => void
  setView: (view: 'boards' | 'board' | 'mindmap' | 'workflow') => void
  setSwitcherOpen: (open: boolean) => void
  /** The signed-in user's own profile: display name and password. */
  profileOpen: boolean
  accountSettingsOpen: boolean
  setAccountSettingsOpen: (open: boolean) => void
  setProfileOpen: (open: boolean) => void
  /** A tenant admin's user management, shown in place of the board list. */
  usersOpen: boolean
  setUsersOpen: (open: boolean) => void
  /** The board whose sharing is being edited, if any. */
  sharingBoardId: string | null
  setSharingBoardId: (boardId: string | null) => void
  /** A project's members or its deletion, or moving an item to another project. */
  projectDialog: ProjectDialog | null
  setProjectDialog: (dialog: ProjectDialog | null) => void
  setExporting: (exporting: boolean) => void
}

/** Discrete steps, so the board lands on predictable sizes rather than drifting. */
export const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5] as const
export const DEFAULT_ZOOM = 1
const ZOOM_KEY = 'blueprint.zoom'

function readZoom(): number {
  try {
    const stored = Number(window.localStorage.getItem(ZOOM_KEY))
    return ZOOM_STEPS.includes(stored as (typeof ZOOM_STEPS)[number]) ? stored : DEFAULT_ZOOM
  } catch {
    return DEFAULT_ZOOM
  }
}

function persistZoom(zoom: number): void {
  try {
    window.localStorage.setItem(ZOOM_KEY, String(zoom))
  } catch {
    // Private browsing or a full quota: the zoom still applies for this session.
  }
}

/** Mirrors the appearance preference in theme-store.ts: never throws, defaults to on. */
const QUICK_INSERT_KEY = 'blueprint.quickInsert'

function readQuickInsert(): boolean {
  try {
    // Absent means never chosen, which takes the default. An explicit "false" still wins.
    const stored = window.localStorage.getItem(QUICK_INSERT_KEY)
    return stored === null ? true : stored === 'true'
  } catch {
    return true
  }
}

function persistQuickInsert(on: boolean): void {
  try {
    window.localStorage.setItem(QUICK_INSERT_KEY, String(on))
  } catch {
    // Private browsing or a full quota: the setting still applies for this session.
  }
}

const LINK_SYNC_KEY = 'blueprint.linkSync'

/** Like Quick Insert: never throws, and each half defaults to on. */
function readLinkSync(): LinkSync {
  try {
    const stored = JSON.parse(window.localStorage.getItem(LINK_SYNC_KEY) ?? '{}') as Partial<LinkSync>
    return { labels: stored.labels !== false, colours: stored.colours !== false }
  } catch {
    return { labels: true, colours: true }
  }
}

function persistLinkSync(sync: LinkSync): void {
  try {
    window.localStorage.setItem(LINK_SYNC_KEY, JSON.stringify(sync))
  } catch {
    // Private browsing or a full quota: the setting still applies for this session.
  }
}

export const useUiStore = create<UiStore>((set, get) => ({
  view: 'boards',
  profileOpen: false,
  accountSettingsOpen: false,
  usersOpen: false,
  sharingBoardId: null,
  projectDialog: null,
  switcherOpen: false,
  exporting: false,
  filters: EMPTY_FILTERS,
  editor: null,
  moveCardId: null,
  promptCardId: null,
  promptScope: null,
  setPromptScope: (promptScope) => set({ promptScope }),
  contextOwner: null,
  headingNotes: null,
  setHeadingNotes: (headingNotes) => set({ headingNotes }),
  setContextOwner: (contextOwner) => set({ contextOwner }),
  reconciliation: null,
  setReconciliation: (reconciliation) => set({ reconciliation }),
  importOpen: false,
  boardNameDialog: null,
  confirm: null,
  appearanceOpen: false,
  quickInsert: typeof window === 'undefined' ? true : readQuickInsert(),
  linkSync: typeof window === 'undefined' ? { labels: true, colours: true } : readLinkSync(),
  focusCardId: null,
  minimapOpen: false,
  zoom: typeof window === 'undefined' ? DEFAULT_ZOOM : readZoom(),

  setFilter: (key, value) => set((state) => ({ filters: { ...state.filters, [key]: value } })),
  clearFilters: () => set({ filters: EMPTY_FILTERS }),
  openEditor: (editor) => set({ editor }),
  closeEditor: () => set({ editor: null }),
  openMove: (moveCardId) => set({ moveCardId }),
  closeMove: () => set({ moveCardId: null }),
  openPrompt: (promptCardId) => set({ promptCardId }),
  closePrompt: () => set({ promptCardId: null }),
  setImportOpen: (importOpen) => set({ importOpen }),
  setBoardNameDialog: (boardNameDialog) => set({ boardNameDialog }),
  requestConfirm: (confirm) => set({ confirm }),
  closeConfirm: () => set({ confirm: null }),
  setAppearanceOpen: (appearanceOpen) => set({ appearanceOpen }),
  setQuickInsert: (quickInsert) => {
    persistQuickInsert(quickInsert)
    set({ quickInsert })
  },
  setLinkSync: (change) => {
    const linkSync = { ...get().linkSync, ...change }
    persistLinkSync(linkSync)
    set({ linkSync })
  },
  setFocusCardId: (focusCardId) => set({ focusCardId }),
  setMinimapOpen: (minimapOpen) => set({ minimapOpen }),
  setZoom: (zoom) => {
    persistZoom(zoom)
    set({ zoom })
  },
  setView: (view) => set({ view }),
  setSwitcherOpen: (switcherOpen) => set({ switcherOpen }),
  setProfileOpen: (profileOpen) => set({ profileOpen }),
  setAccountSettingsOpen: (accountSettingsOpen) => set({ accountSettingsOpen }),
  setUsersOpen: (usersOpen) => set({ usersOpen }),
  setSharingBoardId: (sharingBoardId) => set({ sharingBoardId }),
  setProjectDialog: (projectDialog) => set({ projectDialog }),
  setExporting: (exporting) => set({ exporting }),
}))

export function filtersAreActive(filters: Filters): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.status !== '' ||
    filters.priority !== '' ||
    filters.moscow !== '' ||
    filters.owner.trim().length > 0 ||
    filters.tag.trim().length > 0 ||
    filters.release.trim().length > 0
  )
}
